import { S } from '../core/state.js';
import { fU, fT, esc, toUSDStrict } from '../core/utils.js';
import { _safeChart, _safeDestroy } from '../ui/charts.js';

let chDP = null;
let dpCustomOrder = [];// for custom strategy: array of loan def IDs in user order

function getActiveLoans() {
  return S.expenseDefs.filter(d => d.type === 'Loan' && d.active !== false && d.loanRemain > 0 && d.fixedAmount > 0);
}

function runDebtPayoff() {
  const loans = getActiveLoans();
  const noEl = document.getElementById('dpNoLoans');
  const sumEl = document.getElementById('dpSummary');
  if (!loans.length) { noEl.style.display = ''; sumEl.style.display = 'none'; return; }
  noEl.style.display = 'none'; sumEl.style.display = '';

  const strategy = document.getElementById('dpStrategy').value;
  const extraMonthly = parseFloat(document.getElementById('dpExtra').value) || 0;
  document.getElementById('dpCustomHint').style.display = strategy === 'custom' ? '' : 'none';

  // Build loan objects with remaining balance estimation
  // If interest rate is set, compute remaining principal via amortization formula:
  //   PV = PMT × [(1 - (1+r)^-n) / r]   where r = monthly rate, n = remaining months
  // Otherwise fall back to flat: totalOwed = monthly × remain
  let loanObjs = loans.map(d => {
    const monthly = d.fixedAmount;
    const remain = d.loanRemain;
    const annualRate = d.interestRate || 0;
    let totalOwed;
    if (annualRate > 0) {
      const r = annualRate / 100 / 12; // monthly interest rate
      totalOwed = monthly * (1 - Math.pow(1 + r, -remain)) / r;
    } else {
      totalOwed = monthly * remain;
    }
    return {
      id: d.id, name: d.name, monthly, remain, totalOwed,
      rate: annualRate, monthlyRate: annualRate > 0 ? annualRate / 100 / 12 : 0,
      origRemain: remain, origTotal: totalOwed
    };
  });

  // Sort by strategy
  if (strategy === 'snowball') {
    loanObjs.sort((a, b) => a.totalOwed - b.totalOwed);// smallest balance first
  } else if (strategy === 'avalanche') {
    // Sort by interest rate (highest first) if available, else fallback to highest payment
    loanObjs.sort((a, b) => {
      const rA = a.rate || 0, rB = b.rate || 0;
      if (rA !== rB) return rB - rA;// highest rate first (true avalanche)
      return b.monthly - a.monthly;// tiebreaker: highest payment
    });
  } else {
    // custom: use dpCustomOrder if set, else keep current order
    if (dpCustomOrder.length) {
      loanObjs.sort((a, b) => {
        const ai = dpCustomOrder.indexOf(a.id);
        const bi = dpCustomOrder.indexOf(b.id);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    }
  }

  // Simulate month-by-month payoff with extra payment cascading
  const maxMonths = 360;// safety cap
  const timeline = [];// [{month, loanBalances:{id:remaining}, totalDebt}]
  let balances = {};
  loanObjs.forEach(l => { balances[l.id] = l.totalOwed; });
  let payoffMonths = {};// id -> month number when paid off
  let totalPaidWithExtra = {};// id -> total paid
  loanObjs.forEach(l => { totalPaidWithExtra[l.id] = 0; });

  const totalDebtStart = loanObjs.reduce((s, l) => s + l.totalOwed, 0);
  timeline.push({ month: 0, totalDebt: totalDebtStart, balances: { ...balances } });

  // Track cumulative freed payments from paid-off loans (cascade effect)
  let cascadeExtra = 0;

  let totalInterestPaid = {};
  loanObjs.forEach(l => { totalInterestPaid[l.id] = 0; });

  for (let mo = 1; mo <= maxMonths; mo++) {
    // Check if all paid
    const allPaid = Object.values(balances).every(b => b <= 0);
    if (allPaid) break;

    let extraPool = extraMonthly + cascadeExtra;

    // Step 1: Accrue interest on each loan's remaining balance
    for (const l of loanObjs) {
      if (balances[l.id] <= 0 || !l.monthlyRate) continue;
      const interest = balances[l.id] * l.monthlyRate;
      balances[l.id] += interest;
      totalInterestPaid[l.id] += interest;
    }

    // Step 2: Pay minimum on each loan first
    for (const l of loanObjs) {
      if (balances[l.id] <= 0) continue;
      const pay = Math.min(l.monthly, balances[l.id]);
      balances[l.id] -= pay;
      totalPaidWithExtra[l.id] += pay;
      if (balances[l.id] <= 0 && !payoffMonths[l.id]) payoffMonths[l.id] = mo;
    }

    // Step 3: Distribute extra payment to highest-priority remaining loan
    for (const l of loanObjs) {
      if (balances[l.id] <= 0 || extraPool <= 0) continue;
      const pay = Math.min(extraPool, balances[l.id]);
      balances[l.id] -= pay;
      extraPool -= pay;
      totalPaidWithExtra[l.id] += pay;
      if (balances[l.id] <= 0 && !payoffMonths[l.id]) payoffMonths[l.id] = mo;
    }

    // Step 4: When a loan is paid off, its monthly payment cascades to remaining loans
    for (const l of loanObjs) {
      if (balances[l.id] <= 0 && payoffMonths[l.id] === mo) {
        cascadeExtra += l.monthly;
      }
    }

    const totalDebt = Object.values(balances).reduce((s, b) => s + Math.max(0, b), 0);
    timeline.push({ month: mo, totalDebt, balances: { ...balances } });
  }

  // No-extra baseline: how long without extra payments
  let basePayoffMax = loanObjs.reduce((mx, l) => Math.max(mx, l.origRemain), 0);

  // Calculate months saved and interest savings per loan
  const results = loanObjs.map((l, i) => {
    const payoffMo = payoffMonths[l.id] || l.origRemain;
    const monthsSaved = l.origRemain - payoffMo;
    // Interest saved: if we have actual rate, compute baseline interest vs actual
    // Baseline interest = total payments without extra - principal
    // With extra: we tracked totalInterestPaid already
    let interestSaved;
    if (l.monthlyRate > 0) {
      // Baseline total interest without extra payments = (monthly × origRemain) - principal
      const baselineTotalPaid = l.monthly * l.origRemain;
      const baselineInterest = baselineTotalPaid - l.origTotal;
      interestSaved = Math.max(0, baselineInterest - totalInterestPaid[l.id]);
    } else {
      interestSaved = monthsSaved * l.monthly; // rough: months saved × monthly payment
    }
    return { ...l, payoffMo, monthsSaved, interestSaved, totalInterest: totalInterestPaid[l.id], order: i + 1 };
  });

  const totalMonthly = results.reduce((s, r) => s + r.monthly, 0);
  const _totalRemain = results.reduce((s, r) => s + r.origRemain, 0);
  const totalOwed = results.reduce((s, r) => s + r.origTotal, 0);
  const lastPayoff = Math.max(...results.map(r => r.payoffMo));
  const totalSaved = results.reduce((s, r) => s + r.interestSaved, 0);

  // Format payoff month as date
  const payoffDate = (mo) => {
    const d = new Date(); d.setMonth(d.getMonth() + mo);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
  };

  // Summary cards
  document.getElementById('dpCards').innerHTML = `
    <div class="neo-cell" style="border-left:3px solid var(--ora)"><div class="l">Total Debt</div><div class="v">${fT(totalOwed)}</div><div class="s">${fU(toUSDStrict(totalOwed, 'TRY'))}</div></div>
    <div class="neo-cell" style="border-left:3px solid var(--grn)"><div class="l">Debt-Free By</div><div class="v" style="color:var(--grn)">${payoffDate(lastPayoff)}</div><div class="s">${lastPayoff} months${extraMonthly > 0 ? ' (was ' + basePayoffMax + ')' : ''}</div></div>
    <div class="neo-cell" style="border-left:3px solid var(--yel)"><div class="l">Monthly Payments</div><div class="v">${fT(totalMonthly)}</div><div class="s">${extraMonthly > 0 ? '+' + fT(extraMonthly) + ' extra' : ''}</div></div>
    <div class="neo-cell" style="border-left:3px solid ${totalSaved > 0 ? 'var(--grn)' : 'var(--t3)'}"><div class="l">Est. Saved (w/ extra)</div><div class="v ${totalSaved > 0 ? 'pos' : ''}">${totalSaved > 0 ? fT(totalSaved) : '—'}</div><div class="s">${totalSaved > 0 ? Math.round(totalSaved / totalMonthly) + ' months of payments' : extraMonthly > 0 ? 'No savings yet' : 'Add extra to see savings'}</div></div>`;

  // Chart: total debt over time
  chDP = _safeDestroy(chDP);
  const chartLabels = timeline.map(t => {
    if (t.month === 0) return 'Now';
    const d = new Date(); d.setMonth(d.getMonth() + t.month);
    return d.toLocaleDateString('en', { month: 'short', year: '2-digit' });
  });
  // Also per-loan stacked area
  const datasets = loanObjs.map((l, i) => {
    const colors = ['#D4A853', '#FF6B8A', '#a78bfa', '#4DC9F6', '#FF8C42', '#7ff0c4'];
    const c = colors[i % colors.length];
    return {
      label: l.name, data: timeline.map(t => Math.max(0, t.balances[l.id] || 0)),
      backgroundColor: c + '33', borderColor: c, borderWidth: 1.5, fill: true, tension: .3, pointRadius: 0
    };
  });
  chDP = _safeChart('chDebtPayoff', {
    type: 'line',
    data: { labels: chartLabels, datasets },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { color: 'rgba(255,255,255,.45)', font: { size: 10 }, usePointStyle: true } },
        tooltip: {
          backgroundColor: '#262220', titleColor: '#ffffff', bodyColor: 'rgba(255,255,255,.65)', mode: 'index', intersect: false,
          callbacks: { label: ctx => { const v = ctx.parsed.y; return ctx.dataset.label + ': ₺' + v.toLocaleString('tr-TR', { minimumFractionDigits: 0 }); } }
        }
      },
      scales: {
        x: { ticks: { color: 'rgba(255,255,255,.3)', font: { size: 9 }, maxRotation: 45 }, grid: { display: false } },
        y: { stacked: true, ticks: { color: 'rgba(255,255,255,.3)', font: { size: 10 }, callback: v => '₺' + v.toLocaleString() }, grid: { color: 'rgba(255,255,255,.04)' } }
      }
    }
  });

  // Detail table
  document.getElementById('dpBody').innerHTML = results.map(r => {
    const arrow = strategy === 'custom' ?
      `<span style="cursor:pointer;font-size:14px" data-action="dpMoveUp" data-args='["${r.id}"]'>▲</span> <span style="cursor:pointer;font-size:14px" data-action="dpMoveDown" data-args='["${r.id}"]'>▼</span>` :
      '<span style="color:var(--t3)">#' + r.order + '</span>';
    return `<tr>
      <td><strong>${esc(r.name)}</strong></td>
      <td class="tr">${fT(r.monthly)}</td>
      <td class="tr">${r.origRemain} mo</td>
      <td class="tr">${fT(r.origTotal)}</td>
      <td class="tr" style="color:var(--grn)">${payoffDate(r.payoffMo)}</td>
      <td class="tr ${r.interestSaved > 0 ? 'pos' : ''}">${r.interestSaved > 0 ? fT(r.interestSaved) : '—'}</td>
      <td class="tr">${arrow}</td>
    </tr>`;
  }).join('');

  // Footer totals
  document.getElementById('dpTotMonthly').textContent = fT(totalMonthly);
  document.getElementById('dpTotRemain').textContent = basePayoffMax + ' mo';
  document.getElementById('dpTotOwed').textContent = fT(totalOwed);
  document.getElementById('dpTotPayoff').textContent = payoffDate(lastPayoff);
  document.getElementById('dpTotSaved').textContent = totalSaved > 0 ? fT(totalSaved) : '—';

  // Update custom order
  dpCustomOrder = results.map(r => r.id);
}

function dpMoveUp(id) {
  const idx = dpCustomOrder.indexOf(id);
  if (idx <= 0) return;
  [dpCustomOrder[idx - 1], dpCustomOrder[idx]] = [dpCustomOrder[idx], dpCustomOrder[idx - 1]];
  document.getElementById('dpStrategy').value = 'custom';
  runDebtPayoff();
}

function dpMoveDown(id) {
  const idx = dpCustomOrder.indexOf(id);
  if (idx < 0 || idx >= dpCustomOrder.length - 1) return;
  [dpCustomOrder[idx], dpCustomOrder[idx + 1]] = [dpCustomOrder[idx + 1], dpCustomOrder[idx]];
  document.getElementById('dpStrategy').value = 'custom';
  runDebtPayoff();
}

export { getActiveLoans, runDebtPayoff, dpMoveUp, dpMoveDown };
